import BlockIcon from "@mui/icons-material/Block";
import { Box } from "@mui/material";

import { Text } from "../../components/Text";
import type { Exclusion } from "../api/useWorkerBlockedStatus";

export interface ChatBlockedStateProps {
  exclusion?: Exclusion;
  workerName: string;
}

function InfoBox(props: { label: string; content: string }) {
  return (
    <Box
      sx={(theme) => ({
        padding: 3,
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.borderRadius?.medium,
        maxWidth: "400px",
      })}
    >
      <Text
        semibold
        variant="caption"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          display: "block",
          marginBottom: 1,
        })}
      >
        {props.label}:
      </Text>
      <Text
        variant="body2"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
        })}
      >
        {props.content}
      </Text>
    </Box>
  );
}

/**
 * Display a message when chat is disabled because the worker is blocked/excluded.
 * Shows the reason for the exclusion if available.
 */
export function ChatBlockedState(props: ChatBlockedStateProps) {
  const { exclusion, workerName } = props;

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: 4,
        gap: 2,
        textAlign: "center",
        backgroundColor: theme.palette.background.default,
        borderRadius: theme.borderRadius?.medium,
      })}
    >
      <BlockIcon
        sx={(theme) => ({
          fontSize: "32px",
          color: theme.palette.error.main,
        })}
      />
      <Box>
        <Text
          semibold
          variant="body1"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            marginBottom: 1,
          })}
        >
          Chat Unavailable
        </Text>
        <Text
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
          })}
        >
          {workerName} is currently blocked from this workplace.
        </Text>
      </Box>

      {exclusion?.reason && <InfoBox label="Reason" content={exclusion.reason} />}
      {exclusion?.notes && <InfoBox label="Notes" content={exclusion.notes} />}
    </Box>
  );
}
